import React, { useEffect, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import * as Fa from "react-icons/fa";
import { toast } from "react-toastify";
import { fetchData, postData } from "../../../Services/GlobalService";
import Loader from "../../../Components/Loader";

/* ============================================================
   TYPES
============================================================ */
export interface ConstructOption {
  label: string;   // Name
  value: number;   // Seq
}

export interface ParameterOption {
  label: string;        // Name
  value: number;        // Seq
  constructSeq: number; // ConstructSeq
}

export interface DbCodeOption {
  label: string;  // DB_Code  (shown in dropdown)
  value: string;  // DB_Code  (used as key)
  seq: number;    // Seq
  target: number; // Target   (pre-fills the Target % field)
}

/* ============================================================
   COMPONENT
============================================================ */
const FocusBrandTargetGrowth: React.FC = () => {
  const userId = localStorage.getItem("UserId")?.toString();

  const controllerName          = "DBScoreCard/FocusBrandTargetGrowth";
  const controllerNameConstruct = "DBScoreCard/LBMBrandAndBrandMapping";

  const Endpoint = {
    GetConstructList:           "FetchingConstructData",
    GetParameterList:           "GetParameterByConstructSeqId",
    GetDBbyParamId:             "GetDbByConstructParamId",
    SaveFocusBrandTargetGrowth: "SavingUpdateDBTarget",
  };

  /* ── State ── */
  const [selectedConstruct,  setSelectedConstruct]  = useState<ConstructOption | null>(null);
  const [selectedParameter,  setSelectedParameter]  = useState<ParameterOption | null>(null);
  const [selectedDbCode,     setSelectedDbCode]     = useState<DbCodeOption   | null>(null);
  const [dbCodeList,         setDbCodeList]         = useState<DbCodeOption[]>([]);
  const [dbCodeLoading,      setDbCodeLoading]      = useState<boolean>(false);
  const [targetPercent,      setTargetPercent]      = useState<number | null>(null);
  const [constructList,      setConstructList]      = useState<ConstructOption[]>([]);
  const [parameterList,      setParameterList]      = useState<ParameterOption[]>([]);
  const [parameterLoading,   setParameterLoading]   = useState<boolean>(false);
  const [submitted,          setSubmitted]          = useState<boolean>(false);
  const [errors,             setErrors]             = useState<Record<string, string>>({});
  const [saveLoading,        setSaveLoading]        = useState<boolean>(false);
  const [loader,             setLoader]             = useState<boolean>(false);

  /* ── 1. Load Construct list on mount (ChannelType = B2C hardcoded) ── */
  useEffect(() => {
    const load = async () => {
      setLoader(true);
      try {
        const res = await fetchData(
          controllerNameConstruct,
          Endpoint.GetConstructList,
          "ChannelType=B2C"
        );
        setConstructList(
          (res.Data ?? []).map((d: any) => ({ label: d.Name, value: d.Seq }))
        );
      } catch (error: any) {
        toast.error(error?.response?.data?.Message ?? "Failed to load constructs");
      } finally {
        setLoader(false);
      }
    };
    load();
  }, []);

  /* ── 2. Load Parameters whenever Construct changes ── */
  useEffect(() => {
    if (!selectedConstruct) {
      setParameterList([]);
      setSelectedParameter(null);
      setDbCodeList([]);
      setSelectedDbCode(null);
      setTargetPercent(null);
      return;
    }
    const load = async () => {
      setParameterLoading(true);
      setSelectedParameter(null);
      setDbCodeList([]);
      setSelectedDbCode(null);
      setTargetPercent(null);
      try {
        const res = await fetchData(
          controllerName,
          Endpoint.GetParameterList,
          `ConstructSeq=${selectedConstruct}&Flag=1`
        );
        setParameterList(
          (res.Data ?? []).map((d: any) => ({
            label: d.Name,
            value: d.Seq,
            constructSeq: d.ConstructSeq,
          }))
        );
      } catch (error: any) {
        toast.error(error?.response?.data?.Message ?? "Failed to load parameters");
      } finally {
        setParameterLoading(false);
      }
    };
    load();
  }, [selectedConstruct]);

  /* ── 3. Load DB Code list whenever Parameter changes ── */
  useEffect(() => {
    if (!selectedParameter) {
      setDbCodeList([]);
      setSelectedDbCode(null);
      setTargetPercent(null);
      return;
    }
    const load = async () => {
      setDbCodeLoading(true);
      setDbCodeList([]);
      setSelectedDbCode(null);
      setTargetPercent(null);
      try {
        const res = await fetchData(
          controllerName,
          Endpoint.GetDBbyParamId,
          `PvcConstructId=${selectedConstruct}&PvcParamCode=${selectedParameter}`
        );
        setDbCodeList(
          (res.Data ?? []).map((d: any) => ({
            label:  String(d.DB_Code),
            value:  String(d.DB_Code),
            seq:    d.Seq,
            target: d.Target ?? 0,
          }))
        );
      } catch (error: any) {
        toast.error(error?.response?.data?.Message ?? "Failed to fetch DB Codes");
      } finally {
        setDbCodeLoading(false);
      }
    };
    load();
  }, [selectedParameter]);

const handleDbCodeChange = (dbCode: string) => {
  
  const matched = dbCodeList.find((item) => item.value === dbCode);
  setSelectedDbCode(matched ?? null);
  setTargetPercent(matched?.target ?? null);
  clearError("selectedDbCode");
  clearError("targetPercent");
};

  /* ── Helpers ── */
  const clearError = (field: string) =>
    setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });

  /* ── Validation ── */
  const validateForm = () => {
    const e: Record<string, string> = {};
    if (!selectedConstruct) e.selectedConstruct = "Please select a Construct";
    if (!selectedParameter) e.selectedParameter = "Please select a Parameter";
    if (!selectedDbCode)    e.selectedDbCode    = "Please select a DB Code";
    if (targetPercent === null || targetPercent === undefined)
      e.targetPercent = "Target % is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── 5. Save ── */
  const handleSave = async () => {
    setSubmitted(true);
    if (!validateForm()) return;
    setSaveLoading(true);
    try {
      const payload = {
        pvcSeq:    String(selectedConstruct),
        pvcDbCode: selectedDbCode!.value,
        pvcTarget: String(targetPercent),
      };
      const res = await postData(controllerName, Endpoint.SaveFocusBrandTargetGrowth, payload);
      toast.success(res?.Message ?? "Data saved successfully.");
    } catch (error: any) {
      toast.error(error?.response?.data?.Message ?? "Save failed");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedConstruct(null);
    setSelectedParameter(null);
    setDbCodeList([]);
    setSelectedDbCode(null);
    setTargetPercent(null);
    setSubmitted(false);
    setErrors({});
  };

  /* ============================================================
     RENDER
  ============================================================ */
  if (loader) return <Loader />;

  return (
    <main>
      <div className="col-12 mb-5">
        {/* ── Header ── */}
        <div className="row">
          <div className="col-lg-12">
            <nav aria-label="breadcrumb" className="drop-in">
              <ol className="breadcrumb mx-0">
                <li className="breadcrumb-item bold">DB SIP</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Focus Brand Target Growth
                </li>
              </ol>
            </nav>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <h3 className="py-2">Focus Brand Target Growth</h3>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="text-end pt-2">
              <button
                type="button"
                className="btn anime-secondary-btn me-2"
                onClick={handleReset}
              >
                Reset <Fa.FaSync className="ms-2" />
              </button>
            </div>
          </div>
        </div>

        {/* ── Form ── */}
        <section>
          <div className="sales-box">
            <div className="row">
              {/* Construct */}
              <div className="col-lg-3 col-md-6 col-sm-12">
                <label className="form-label">Construct</label>
                <Dropdown
                  value={selectedConstruct}
                  onChange={(e) => {
                    setSelectedConstruct(e.value);
                    clearError("selectedConstruct");
                  }}
                  options={constructList}
                  optionLabel="label"
                  placeholder="Select Construct"
                  className={
                    submitted && errors.selectedConstruct
                      ? "p-invalid w-100"
                      : "w-100"
                  }
                />
                {submitted && errors.selectedConstruct && (
                  <small className="p-error">{errors.selectedConstruct}</small>
                )}
              </div>

              {/* Parameter */}
              <div className="col-lg-3 col-md-6 col-sm-12">
                <label className="form-label">Parameter</label>
                <Dropdown
                  value={selectedParameter}
                  onChange={(e) => {
                    setSelectedParameter(e.value);
                    clearError("selectedParameter");
                  }}
                  options={parameterList}
                  optionLabel="label"
                  placeholder={
                    parameterLoading ? "Loading..." : "Select Parameter"
                  }
                  disabled={!selectedConstruct || parameterLoading}
                  className={
                    submitted && errors.selectedParameter
                      ? "p-invalid w-100"
                      : "w-100"
                  }
                  dropdownIcon={
                    parameterLoading
                      ? "pi pi-spin pi-spinner"
                      : "pi pi-chevron-down"
                  }
                />
                {submitted && errors.selectedParameter && (
                  <small className="p-error">{errors.selectedParameter}</small>
                )}
              </div>

              {/* DB Code — dropdown, populated from API, auto-fills Target on selection */}
              <div className="col-lg-3 col-md-6 col-sm-12">
                <label className="form-label">DB Code</label>
                <Dropdown
                   
                  value={selectedDbCode?.value ?? null} 
                  options={dbCodeList}
                  onChange={(e) => handleDbCodeChange(e.value)}
                  optionValue="value"  
                  optionLabel="label"
                  placeholder={
                    dbCodeLoading
                      ? "Loading..."
                      : !selectedParameter
                        ? "Select a parameter first"
                        : dbCodeList.length === 0
                          ? "No DB codes found"
                          : "Select DB Code"
                  }
                  disabled={
                    !selectedParameter ||
                    dbCodeLoading ||
                    dbCodeList.length === 0
                  }
                  className={
                    submitted && errors.selectedDbCode
                      ? "p-invalid w-100"
                      : "w-100"
                  }
                  dropdownIcon={
                    dbCodeLoading
                      ? "pi pi-spin pi-spinner"
                      : "pi pi-chevron-down"
                  }
                  filter
                  filterPlaceholder="Search DB Code..."
                />
                {submitted && errors.selectedDbCode && (
                  <small className="p-error">{errors.selectedDbCode}</small>
                )}
              </div>

              {/* Target % — pre-filled from selected DB Code's Target, fully editable */}
              <div className="col-lg-3 col-md-6 col-sm-12">
                <label className="form-label">Target %</label>
                <InputNumber
                  value={targetPercent}
                  onValueChange={(e) => {
                    setTargetPercent(e.value ?? null);
                    clearError("targetPercent");
                  }}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  placeholder="Auto-filled on DB Code selection"
                  disabled={!selectedDbCode}
                  className={
                    submitted && errors.targetPercent
                      ? "p-invalid w-100"
                      : "w-100"
                  }
                />
                {submitted && errors.targetPercent && (
                  <small className="p-error">{errors.targetPercent}</small>
                )}
              </div>

              {/* Save */}
              <div className="col-lg-12">
                <div className="text-end mt-3">
                  <button
                    type="button"
                    className="btn anime-btn"
                    onClick={handleSave}
                    disabled={saveLoading}
                  >
                    {saveLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" />
                        Saving...
                      </>
                    ) : (
                      <>Save</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default FocusBrandTargetGrowth;